import { Component, OnInit, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { ChipType, RecipientBoxChip, RecipientsFormGroup, RecipientsGroup, SendNewEmailDialog } from '@core/models/case-communication/send-new-email-dialog.model';
import { FormGroup } from '@angular/forms';
import { SelectionOption } from '@core/models';

@Component({
    selector: 'app-add-recipients',
    templateUrl: './add-recipients.component.html',
    styleUrls: ['./add-recipients.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddRecipientsComponent implements OnInit {
  static readonly componentName = 'AddRecipientsComponent';
  
  @Input() formGroup: FormGroup<RecipientsFormGroup>;
  @Input() sysText: any;
  @Input() boxLabel: any;
  @Input() dialogData: SendNewEmailDialog;
  @Input() recipientsGroup: RecipientsGroup;

  @ViewChild('select') private select: MatSelect;

  public chipType = ChipType;

  private prevSelectedRelRecs: string[] = [];

  constructor() { }

  ngOnInit(): void {
    if (this.dialogData.automaticCcAddresses && this.recipientsGroup === RecipientsGroup.cc) {
      this.addAddressesToRecipientsBox(this.dialogData.automaticCcAddresses);
    }
    else if (this.dialogData.automaticBccAddresses && this.recipientsGroup === RecipientsGroup.bcc) {
      this.addAddressesToRecipientsBox(this.dialogData.automaticBccAddresses);
    }
  } 

  /* This process does the following:
   * * When a relevant recipient or contact is selected, it is added to
   *   the recipients box if the associated contact + email address is
   *   not already represented in the recipients box. Also, all relevant
   *   recipients with the same contact + email address are also selected.
   * * When a relevant recipient is unselected, all items in the recipients
   *   box that share the same contact + email address are removed.
   *   Additionally, all relevant recipients with the same contact + email
   *   address are also unselected.
   * * When an email address is added, it is added to the recipients box
   *   if the (raw) email address is not already represented in the
   *   recipients box.
   */
  public onSelectionChangeRelevantRecipient(msc: MatSelectChange): void {
    var selectedRelRecs: string[] = msc.value;

    const addedRelRecs: string[] = selectedRelRecs
      .filter(srr => this.prevSelectedRelRecs.find(psrr => srr == psrr) == null);

    const removedRelRecs: string[] = this.prevSelectedRelRecs
      .filter(psrr => selectedRelRecs.find(srr => psrr == srr) == null);

    // add relevant recipients newly selected

    addedRelRecs.forEach(arr => {
      // add to recipients box if contact + email address not represented

      const addedRelRec: SelectionOption = this.dialogData.relevantRecipientOptions
        .find(rco => arr == rco.id + "." + rco.relevantContextId + "." + rco.emailAddressTypeId);

      this.addToRecipientsBox(
        addedRelRec.id as number,
        addedRelRec.description,
        addedRelRec.emailAddress,
        addedRelRec.emailAddressTypeId,
        addedRelRec.relevantContextId,
        ChipType.RelevantRecipient);

      // select all relevant recipients of the same contact + email address

      const sameContactEmailAddressesToAdd: SelectionOption[] = this.dialogData.relevantRecipientOptions
        .filter(rro => addedRelRec.id == rro.id && addedRelRec.emailAddress?.toLowerCase() == rro.emailAddress?.toLowerCase());

      sameContactEmailAddressesToAdd.forEach(scea => {
        if (selectedRelRecs.find(srr => scea.id + "." + scea.relevantContextId + "." + scea.emailAddressTypeId == srr) == null) {
          selectedRelRecs.push(scea.id + "." + scea.relevantContextId + "." + scea.emailAddressTypeId);
        }
      });
    })

    // remove relevant recipients newly unselected

    removedRelRecs.forEach(rrr => {
      var recipientBoxChips: RecipientBoxChip[] = this.formGroup.controls.recipientBoxChips.value;

      const removedRelRec: SelectionOption = this.dialogData.relevantRecipientOptions
        .find(rro => rrr == rro.id + "." + rro.relevantContextId + "." + rro.emailAddressTypeId);

      // remove recipients box chip items with the same contact + email address

      if (recipientBoxChips.find(rbc => removedRelRec.id == rbc.id && removedRelRec.emailAddress.toLowerCase() == rbc.emailAddress.toLowerCase()) != null) {
        const remainingRecipientBoxChips: RecipientBoxChip[] = recipientBoxChips
          .filter(rbc => removedRelRec.id != rbc.id || removedRelRec.emailAddress.toLowerCase() != rbc.emailAddress.toLowerCase());

        recipientBoxChips = remainingRecipientBoxChips;
      }

      // unselect relevant recipient items of the same contact + email address

      const sameContactEmailAddressesToRemove: SelectionOption[] = this.dialogData.relevantRecipientOptions
        .filter(rro => removedRelRec.id == rro.id && removedRelRec.emailAddress.toLowerCase() == rro.emailAddress.toLowerCase());

      sameContactEmailAddressesToRemove.forEach(scea => {
        // make sure this relevant recipient is not selected

        if (selectedRelRecs.find(srr => scea.id + "." + scea.relevantContextId + "." + scea.emailAddressTypeId == srr)) {
          const remainingRelevantRecipientOptions: string[] = selectedRelRecs
            .filter(srr => scea.id + "." + scea.relevantContextId + "." + scea.emailAddressTypeId != srr);
          
          selectedRelRecs = remainingRelevantRecipientOptions;
        }
      });
    })
    
    this.prevSelectedRelRecs = selectedRelRecs;

    setTimeout(() => { this.formGroup.controls.relevantRecipientOptions.setValue(selectedRelRecs); }, 0);
  }

  public onOptionSelectEmployeeRecipientAdd(): void {
    const selectedEro = this.formGroup.controls.employeeRecipientOptionAdd.value;

    // the picked contact will be added to the recipients box if the 
    // contact + email address isn't already represented

    const employeeRecipient: SelectionOption = this.dialogData.employeeRecipientOptions
      .find(ero => selectedEro == ero.id + "." + ero.emailAddressTypeId);

    this.addToRecipientsBox(
      employeeRecipient.id as number, 
      employeeRecipient.description,
      employeeRecipient.emailAddress,
      employeeRecipient.emailAddressTypeId,
      null,
      ChipType.Contact);

    // any relevant recipient with the same contact + email address
    // as the picked contact should appear selected

    const matchingRelevantRecipients: SelectionOption[] = this.dialogData.relevantRecipientOptions
      .filter(rro => employeeRecipient.id == rro.id && employeeRecipient.emailAddress.toLowerCase() == rro.emailAddress.toLowerCase());

    var selectedRelevantRecipients: string[] = this.formGroup.controls.relevantRecipientOptions.value;

    matchingRelevantRecipients.forEach(mrr => {
      if (selectedRelevantRecipients.find(srr => mrr.id + "." + mrr.relevantContextId + "." + mrr.emailAddressTypeId == srr) == null)
        selectedRelevantRecipients.push(mrr.id + "." + mrr.relevantContextId + "." + mrr.emailAddressTypeId);
    });

    this.formGroup.controls.employeeRecipientOptionAdd.setValue(null);

    setTimeout(() => { this.formGroup.controls.relevantRecipientOptions.setValue(selectedRelevantRecipients); }, 0);
  }

  public onAddEmail(): void {
    const emailAddress = this.formGroup.controls.additionalEmailAddressAdd.value;

    if (emailAddress != null) {
      // the email address is added to the box only if the address
      // isn't already represented

      const added: boolean = this.addToRecipientsBox(
        null,
        emailAddress,
        emailAddress,
        null,
        null,
        ChipType.AdditionalEmail);
      
      if (added) {
        this.formGroup.controls.additionalEmailAddresses.value.push(emailAddress);
        this.formGroup.controls.additionalEmailAddressAdd.setValue(null);
      }
    }
  }

  public onRemoveRecipient(recipientBoxChip: RecipientBoxChip): void {
    // the pressed button goes with its chip
    this.select.focus();
    var recipientBoxChips: RecipientBoxChip[] = this.formGroup.controls.recipientBoxChips.value;

    switch (recipientBoxChip.type) {
      case ChipType.RelevantRecipient: {
        // unselect all relevant recipients with the same
        // contact + email address

        const sameContactEmailAddresses: SelectionOption[] = this.dialogData.relevantRecipientOptions
          .filter(rro => recipientBoxChip.id == rro.id && recipientBoxChip.emailAddress.toLowerCase() == rro.emailAddress.toLowerCase());

        sameContactEmailAddresses.forEach(scea => {
          if (this.formGroup.controls.relevantRecipientOptions.value.find(rro => scea.id + "." + scea.relevantContextId + "." + scea.emailAddressTypeId == rro) != null) {
            const updatedOptions: string[] = this.formGroup.controls.relevantRecipientOptions.value
              .filter(rro => scea.id + "." + scea.relevantContextId + "." + scea.emailAddressTypeId != rro);
            
            this.formGroup.controls.relevantRecipientOptions.setValue(updatedOptions);
          }
        });

        // remove the item from the recipients box

        recipientBoxChips = recipientBoxChips
          .filter(rbc => recipientBoxChip.id + "." + recipientBoxChip.relevantContextId + "." + recipientBoxChip.emailAddressTypeId != rbc.id + "." + rbc.relevantContextId + "." + rbc.emailAddressTypeId);

        break;
      }

      case ChipType.Contact: {
        // unselect all relevant recipients with the same
        // contact + email address

        const sameContactEmailAddresses: SelectionOption[] = this.dialogData.relevantRecipientOptions
          .filter(rro => recipientBoxChip.id == rro.id && recipientBoxChip.emailAddress.toLowerCase() == rro.emailAddress.toLowerCase());

        sameContactEmailAddresses.forEach(scea => {
          if (this.formGroup.controls.relevantRecipientOptions.value.find(rro => scea.id + "." + scea.relevantContextId + "." + scea.emailAddressTypeId == rro) != null) {
            const updatedArray: string[] = this.formGroup.controls.relevantRecipientOptions.value
              .filter(rro => scea.id + "." + scea.relevantContextId + "." + scea.emailAddressTypeId != rro);
            
            this.formGroup.controls.relevantRecipientOptions.setValue(updatedArray);
          }
        });

        // remove the item from the recipients box

        recipientBoxChips = recipientBoxChips
          .filter(rbc => rbc.id + "." + rbc.emailAddressTypeId != recipientBoxChip.id + "." + recipientBoxChip.emailAddressTypeId);

        break;
      }

      case ChipType.AdditionalEmail: {
        const updateArray = this.formGroup.controls.additionalEmailAddresses.value
          .filter(aea => aea.toLowerCase() !== recipientBoxChip.emailAddress.toLowerCase());
          
        this.formGroup.controls.additionalEmailAddresses.setValue(updateArray);

        recipientBoxChips = recipientBoxChips
          .filter(rbc => rbc.type != ChipType.AdditionalEmail || rbc.emailAddress.toLowerCase() != recipientBoxChip.emailAddress.toLowerCase());

        break;
      }
    }

    this.formGroup.controls.recipientBoxChips.setValue(recipientBoxChips);
  }

  private addAddressesToRecipientsBox(addresses: string[]): void {
    addresses.forEach(address => {
      this.addToRecipientsBox(null, address, address, null, null, ChipType.AdditionalEmail);
    });
  }

  private addToRecipientsBox(id: number, description: string, emailAddress: string, emailAddressTypeId: number, relevantContextId: number, type: ChipType): boolean {    
    var notInBox: boolean = false;
    var recipientBoxChips: RecipientBoxChip[] = this.formGroup.controls.recipientBoxChips.value;

    if (type == ChipType.AdditionalEmail)
      notInBox = recipientBoxChips
        .find(rbc => type == rbc.type && emailAddress.toLowerCase() == rbc.emailAddress.toLowerCase()) == null;
    else
      notInBox = recipientBoxChips
        .find(rbc => id == rbc.id && emailAddress.toLowerCase() == rbc.emailAddress.toLowerCase()) == null;

    if (notInBox) {
      const newItem: RecipientBoxChip = {
        id: id,
        description: description,
        emailAddress: emailAddress,
        relevantContextId: relevantContextId,
        emailAddressTypeId: emailAddressTypeId,
        type: type
      };

      recipientBoxChips.push(newItem);
      return true;
    }
    else
      return false;
  }
}